import React, { useEffect, useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  useTheme,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenTwoToneIcon from "@mui/icons-material/FolderOpenTwoTone";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { fetchFolderContents, getSharedFoldersByPath } from "../api"; // ✅ usa o mesmo usado no UserSideBar

interface FolderNode {
  name: string;
  path: string;
  subfolders: FolderNode[];
  isLoaded?: boolean;
}

interface FolderTreeProps {
  folders: { path: string; isDirectory: boolean; accessPermission: boolean }[];
  onFolderSelect: (folderPath: string) => void;
  selectedFolder: string;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  onFolderSelect,
  selectedFolder,
}) => {
  const theme = useTheme();
  const [foldersTree, setFoldersTree] = useState<FolderNode[]>([]);
  const [openFolders, setOpenFolders] = useState<{ [path: string]: boolean }>(
    {}
  );

  useEffect(() => {
    // Filtra apenas os diretórios de primeiro nível com permissão
    const roots = folders.filter((f) => f.isDirectory && f.accessPermission);

    const rootNodes: FolderNode[] = roots.map((f) => ({
      name: f.path.split("\\").pop() || "",
      path: f.path,
      subfolders: [],
      isLoaded: false,
    }));

    setFoldersTree(rootNodes);
  }, [folders]);

  const handleToggleExpand = async (node: FolderNode) => {
    const isOpen = openFolders[node.path] || false;

    if (!isOpen && !node.isLoaded) {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");

      try {
        const items = await getSharedFoldersByPath({
          username: user.login,
          path: node.path,
        });

        const subfolders = items
          .filter((i) => i.isDirectory && i.accessPermission)
          .map((sub) => ({
            name: sub.path.split("\\").pop() || "",
            path: sub.path,
            subfolders: [],
            isLoaded: false,
          }));

        const updateTree = (nodes: FolderNode[]): FolderNode[] =>
          nodes.map((n) => {
            if (n.path === node.path) {
              return { ...n, subfolders, isLoaded: true };
            }
            return { ...n, subfolders: updateTree(n.subfolders) };
          });

        setFoldersTree(updateTree(foldersTree));
      } catch (err) {
        console.error("Erro ao carregar subpastas:", err);
      }
    }

    setOpenFolders((prev) => ({
      ...prev,
      [node.path]: !isOpen,
    }));
  };

  const renderFolderNode = (node: FolderNode, level = 0) => {
    const isOpen = openFolders[node.path] || false;
    const isSelected = selectedFolder === node.path;

    return (
      <React.Fragment key={node.path}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => onFolderSelect(node.path)}
            sx={{
              pl: 2 + level * 4,
              backgroundColor: isSelected
                ? theme.palette.action.hover
                : "inherit",
              height: "32px",
            }}
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand(node);
              }}
              sx={{ width: 24, display: "flex", justifyContent: "center" }}
            >
              {node.subfolders.length > 0 || !node.isLoaded ? (
                isOpen ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )
              ) : (
                <Box sx={{ width: 24 }} />
              )}
            </Box>

            <ListItemIcon>
              {isOpen ? <FolderOpenTwoToneIcon /> : <FolderIcon />}
            </ListItemIcon>

            <ListItemText primary={node.name} />
          </ListItemButton>
        </ListItem>

        {node.subfolders.length > 0 && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.subfolders.map((sub) => renderFolderNode(sub, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return <List>{foldersTree.map((node) => renderFolderNode(node))}</List>;
};

export default FolderTree;
